import React from 'react';
import { Calendar, Target, Video, Users, MessageSquare, Zap, FileText, Headphones } from 'lucide-react';
import { Button } from './Button';
import { motion } from 'framer-motion';
interface CreatorLabProgramProps {
  onApplyClick: () => void;
}
export function CreatorLabProgram({
  onApplyClick
}: CreatorLabProgramProps) {
  // All 5 program details in a single array for mobile stacking
  const programDetails = [{
    icon: Calendar,
    text: '4-week cohort'
  }, {
    icon: Target,
    text: 'Run 4–6 Pulse Markets total'
  }, {
    icon: Video,
    text: 'Fully remote (no travel)'
  }, {
    icon: Users,
    text: 'Weekly cohort call (45–60 mins)'
  }, {
    icon: MessageSquare,
    text: 'Optional 1:1 check-ins'
  }];
  const whyJoin = [{
    icon: Zap,
    label: 'Early access + direct support'
  }, {
    icon: FileText,
    label: 'Templates + best practices'
  }, {
    icon: Headphones,
    label: 'Direct line to the team'
  }];
  return <section id="program" className="py-20 lg:py-32 bg-navy-950 relative border-t border-slate-800" data-id="element-110">
      {/* Background Gradient Wash */}
      <div className="absolute inset-0 bg-gradient-to-b from-mint-500/5 via-transparent to-wave-500/5" data-id="element-111" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-mint-500 to-transparent" data-id="element-112" />

      <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-113">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-114">
          {/* Badge with scarcity */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-mint-500/10 to-wave-500/10 border border-mint-500/20 mb-6" data-id="element-115">
            <span className="relative flex h-2 w-2" data-id="element-116">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint-400 opacity-75" data-id="element-117"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-mint-500" data-id="element-118"></span>
            </span>
            <span className="text-mint-500 text-sm font-bold uppercase tracking-wider" data-id="element-119">
              Limited to 5 Creators
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-120">
            Pulsewave Creator Lab
          </h2>

          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-121">
            Run Pulse Markets live with your audience for 4 weeks. Get
            templates, direct support, and leave with a repeatable format you
            can use forever.
          </p>
        </motion.div>

        {/* Centered Program Card with Glow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="relative mb-12" data-id="element-122">
          {/* Glow effect behind card */}
          <div className="absolute inset-0 bg-gradient-to-r from-mint-500/10 to-wave-500/10 blur-3xl rounded-3xl scale-105 opacity-60" data-id="element-123" />

          <div className="relative bg-navy-900 rounded-2xl p-8 lg:p-10 border border-slate-800 shadow-xl" data-id="element-124">
            {/* Gradient top accent */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-mint-500 to-wave-500 rounded-t-2xl" data-id="element-125" />

            {/* Centered Title */}
            <h3 className="text-xl font-bold text-white mb-10 text-center" data-id="element-126">
              Program Details
            </h3>

            {/* Program details - Stacked on mobile with aligned icons, centered rows on desktop */}
            <div className="flex flex-col items-center gap-4 mb-10" data-id="element-127">
              {/* Mobile: Stacked list with aligned icons */}
              <div className="flex flex-col gap-4 w-full max-w-md md:hidden" data-id="element-128">
                {programDetails.map((item, idx) => {
                const Icon = item.icon;
                return <div key={idx} className="flex items-center gap-3" data-id="element-129">
                      <div className="w-9 h-9 rounded-lg bg-navy-950 border border-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-130">
                        <Icon className="w-4 h-4 text-mint-500" data-id="element-131" />
                      </div>
                      <span className="text-slate-300" data-id="element-132">{item.text}</span>
                    </div>;
              })}
              </div>

              {/* Desktop: 3+2 centered rows */}
              <div className="hidden md:flex flex-col items-center gap-6" data-id="element-133">
                {/* Row 1: 3 items */}
                <div className="flex flex-wrap justify-center gap-4 lg:gap-8" data-id="element-134">
                  {programDetails.slice(0, 3).map((item, idx) => {
                  const Icon = item.icon;
                  return <div key={idx} className="flex items-center gap-3" data-id="element-135">
                        <div className="w-9 h-9 rounded-lg bg-navy-950 border border-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-136">
                          <Icon className="w-4 h-4 text-mint-500" data-id="element-137" />
                        </div>
                        <span className="text-slate-300" data-id="element-138">{item.text}</span>
                      </div>;
                })}
                </div>

                {/* Row 2: 2 items */}
                <div className="flex flex-wrap justify-center gap-4 lg:gap-8" data-id="element-139">
                  {programDetails.slice(3).map((item, idx) => {
                  const Icon = item.icon;
                  return <div key={idx} className="flex items-center gap-3" data-id="element-140">
                        <div className="w-9 h-9 rounded-lg bg-navy-950 border border-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-141">
                          <Icon className="w-4 h-4 text-mint-500" data-id="element-142" />
                        </div>
                        <span className="text-slate-300" data-id="element-143">{item.text}</span>
                      </div>;
                })}
                </div>
              </div>
            </div>

            {/* Why Join pills - no label */}
            <div className="pt-8 border-t border-slate-800/50" data-id="element-144">
              <div className="grid sm:grid-cols-3 gap-4" data-id="element-145">
                {whyJoin.map((item, idx) => {
                const Icon = item.icon;
                return <div key={idx} className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-navy-950/50 border border-slate-800/50" data-id="element-146">
                      <Icon className="w-4 h-4 text-mint-500 flex-shrink-0" data-id="element-147" />
                      <span className="text-slate-400 text-sm" data-id="element-148">
                        {item.label}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stipend Callout + CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-149">
          {/* Stipend callout - positioned as hook before CTA */}
          <div className="relative bg-gradient-to-r from-mint-500/10 to-wave-500/10 rounded-xl px-6 py-4 border border-mint-500/30 overflow-hidden" data-id="element-150">
            {/* Subtle glow */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-mint-500/20 blur-2xl rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-151" />

            <div className="relative flex items-center gap-4" data-id="element-152">
              <div data-id="element-153">
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-mint-500 to-wave-500" data-id="element-154">
                  $2,000
                </div>
                <div className="text-sm font-medium text-white" data-id="element-155">stipend</div>
              </div>
              <div className="w-px h-10 bg-slate-700" data-id="element-156" />
              <div className="text-slate-400 text-xs leading-relaxed" data-id="element-157">
                Paid upon
                <br data-id="element-158" />
                completion
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button variant="primary" onClick={onApplyClick} className="bg-gradient-to-r from-mint-500 to-wave-500 border-none text-navy-950 font-bold text-lg px-10 py-4 hover:shadow-xl hover:shadow-mint-500/30 transition-all duration-300" data-id="element-159">
            Apply to the Creator Lab
          </Button>
        </motion.div>
      </div>
    </section>;
}